/**
 * Data Import Wizard — tab-close guard dialog (finding #2). Shown when a user
 * closes a wizard tab while an import is still running: closing would silently
 * detach the live import. Mirrors `TransactionCloseDialog`/`NotebookCloseDialog`
 * — confirm cancels the run (via the existing cancel IPC) then closes; decline
 * keeps the tab open.
 */
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogFooter,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogCancel,
} from '../../../components/ui/alert-dialog';
import { Button } from '../../../components/ui/button';

interface ImportCloseDialogProps {
  open: boolean;
  onCancelRunAndClose: () => void;
  onKeepOpen: () => void;
  isProcessing?: boolean;
}

export function ImportCloseDialog({
  open,
  onCancelRunAndClose,
  onKeepOpen,
  isProcessing = false,
}: ImportCloseDialogProps) {
  return (
    <AlertDialog open={open} onOpenChange={(isOpen) => !isOpen && onKeepOpen()}>
      <AlertDialogContent data-testid="data-import-close-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle>An import is still running</AlertDialogTitle>
          <AlertDialogDescription>
            Closing this tab will cancel the import in progress. Any rows already committed stay in the
            table. Cancel it and close?
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter className="gap-2 sm:gap-0">
          <AlertDialogCancel onClick={onKeepOpen} disabled={isProcessing} data-testid="data-import-close-keep">
            Keep importing
          </AlertDialogCancel>
          <Button
            variant="destructive"
            onClick={onCancelRunAndClose}
            disabled={isProcessing}
            data-testid="data-import-close-cancel-run"
          >
            Cancel import & close
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
